// ─────────────────────────────────────────────────────────────────────────────
// BIZPLUS PARITY — lost reason capture
//
// Shown when a deal is moved to Closed Lost. The reason cannot be supplied
// later in practice — nobody reopens a closed deal to record why it died — so
// this is the one moment it can be captured, and the dialog does not offer a
// way past it.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { lostNoteProblem, LOST_NOTE_MIN } from '../lost-note';
import type { Deal } from '../types';

interface Props {
  deal: Deal;
  onCancel: () => void;
  onConfirm: (payload: { lostReasonId: string; lostReasonNote: string; closeDeal: boolean }) => void;
  isSaving?: boolean;
  /** Shows the "also close the deal?" choice — only meaningful when this
   *  modal is closing a DOCUMENT (quotation/proforma) that sits in front of
   *  a deal, not when it's closing the deal/lead itself directly (Pipeline,
   *  Deals list) — those callers never pass this, so their behavior and
   *  copy are unchanged. Rejecting one quotation doesn't always mean the
   *  deal is dead; the customer may still take a re-quote. */
  closeDealChoice?: { dealTitle: string };
}

export function LostReasonModal({ deal, onCancel, onConfirm, isSaving, closeDealChoice }: Props) {
  const [lostReasonId, setReason] = useState('');
  const [lostReasonNote, setNote] = useState('');
  const [closeDeal, setCloseDeal] = useState(true);
  const [touched, setTouched] = useState(false);
  // A list reason AND a real note (client, 2026-10-29).
  const noteProblem = lostNoteProblem(lostReasonNote);

  const { data, isLoading } = useQuery({
    queryKey: ['deals', 'lost-reasons'],
    queryFn: async () => (await api.get('/api/deals-outcome/lost-reasons')).data,
  });

  const reasons: Array<{ _id: string; name: string }> = data?.items || [];

  return (
    <Modal open onClose={onCancel} title={`Why was "${deal.title}" lost?`}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          This is recorded against the deal and drives the win/loss report. It cannot
          realistically be added later.
        </p>

        {isLoading && <p className="text-sm text-muted-foreground">Loading reasons…</p>}

        {!isLoading && reasons.length === 0 && (
          // Said explicitly rather than showing an empty dropdown that looks broken.
          <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/40 p-3 text-sm text-amber-900 dark:text-amber-200">
            No lost reasons are configured yet. Add them under Master Data before closing
            deals as lost, otherwise the report has nothing to group by.
          </div>
        )}

        {reasons.length > 0 && (
          <label className="block">
            <span className="text-sm font-medium block mb-1">Reason</span>
            <select value={lostReasonId} onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm">
              <option value="">Select…</option>
              {reasons.map((r) => <option key={r._id} value={r._id}>{r.name}</option>)}
            </select>
          </label>
        )}

        <label className="block">
          <span className="text-sm font-medium block mb-1">What happened? <span className="text-red-600">*</span></span>
          <textarea value={lostReasonNote} onChange={(e) => setNote(e.target.value)} onBlur={() => setTouched(true)} rows={3}
            placeholder="What specifically happened? e.g. bought from another supplier at Rs 8 less per litre"
            className="w-full rounded-md border px-3 py-2 text-sm" />
          <span className={`mt-1 block text-xs ${touched && noteProblem ? 'text-red-600' : 'text-muted-foreground'}`}>
            {touched && noteProblem ? noteProblem : `Required — at least ${LOST_NOTE_MIN} characters. "close" is not accepted.`}
          </span>
        </label>

        {closeDealChoice && (
          <div className="rounded-md border p-3 space-y-2">
            <span className="text-sm font-medium block">What should happen to the deal?</span>
            <label className="flex items-start gap-2 text-sm">
              <input type="radio" className="mt-0.5" checked={closeDeal} onChange={() => setCloseDeal(true)} />
              <span>
                Close <strong>"{closeDealChoice.dealTitle}"</strong> as lost too — the opportunity itself is over,
                not just this document.
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input type="radio" className="mt-0.5" checked={!closeDeal} onChange={() => setCloseDeal(false)} />
              <span>
                Only mark this document as lost — keep the deal open (e.g. the customer may still take a re-quote).
              </span>
            </label>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button
            onClick={() => { setTouched(true); if (!noteProblem) onConfirm({ lostReasonId, lostReasonNote: lostReasonNote.trim(), closeDeal: closeDealChoice ? closeDeal : true }); }}
            disabled={!lostReasonId || !!noteProblem || isSaving}
          >
            {isSaving ? 'Saving…' : 'Mark as lost'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default LostReasonModal;
